import { useLayoutEffect } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

type SrpScrollData = { type: 'list'; maloneLotId: string } | { type: 'feed'; index: number };

type ListScrollRestoreOptions = {
  getScrollContainer: () => HTMLElement | null;
  offsetPx?: number;
  timeoutMs?: number;
};

type ListScrollRestoreResult = {
  cleanup: () => void;
  hadSavedPosition: boolean;
};

type FeedRestoreOptions = {
  listingsCount: number;
  feedHeight: string | null;
  scrollToIndex: (index: number, options?: { behavior?: ScrollBehavior }) => void;
  scrollerElRef: React.RefObject<HTMLDivElement | null>;
  isMobile: boolean;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SRP_SCROLL_KEY = 'hp-srp-scroll-position';
const DEFAULT_TIMEOUT_MS = 10_000;
const MD_BREAKPOINT = 768;

// ─── Save / Read / Clear ─────────────────────────────────────────────────────

export function saveSrpScrollPosition(data: SrpScrollData): void {
  try {
    sessionStorage.setItem(SRP_SCROLL_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage unavailable (private mode, quota exceeded)
  }
}

export function readSrpScrollPosition(): SrpScrollData | null {
  try {
    const raw = sessionStorage.getItem(SRP_SCROLL_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SrpScrollData;
  } catch {
    return null;
  }
}

export function clearSrpScrollPosition(): void {
  try {
    sessionStorage.removeItem(SRP_SCROLL_KEY);
  } catch {
    // noop
  }
}

// ─── List View Restore (imperative, for class components) ────────────────────

function scrollContainerTo(container: HTMLElement | null, offsetTop: number, offsetPx: number): void {
  if (!container) return;
  container.scrollTop = offsetTop - offsetPx;
}

export function restoreSrpListScroll(options: ListScrollRestoreOptions): ListScrollRestoreResult {
  const noop = { cleanup: () => {}, hadSavedPosition: false };

  const saved = readSrpScrollPosition();
  if (!saved || saved.type !== 'list') return noop;

  const { maloneLotId } = saved;
  clearSrpScrollPosition();

  const { getScrollContainer, offsetPx = 0, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Try immediately (covers SSR case where listings are already in DOM)
  const existingEl = document.getElementById(maloneLotId);
  if (existingEl) {
    requestAnimationFrame(() => {
      scrollContainerTo(getScrollContainer(), existingEl.offsetTop, offsetPx);
    });
    return { cleanup: () => {}, hadSavedPosition: true };
  }

  // Element not found yet — use MutationObserver to wait for it
  let cleaned = false;
  let observer: MutationObserver | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  observer = new MutationObserver(() => {
    if (cleaned) return;
    const el = document.getElementById(maloneLotId);
    if (!el) return;

    cleanup();
    requestAnimationFrame(() => {
      scrollContainerTo(getScrollContainer(), el.offsetTop, offsetPx);
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
  timer = setTimeout(cleanup, timeoutMs);

  return { cleanup, hadSavedPosition: true };
}

// ─── Feed View: Read saved index (pure function) ────────────────────────────

export function getSavedFeedIndex(maxIndex: number): number {
  const saved = readSrpScrollPosition();
  if (!saved || saved.type !== 'feed') return 0;
  const { index } = saved;
  return index >= 0 && index < maxIndex ? index : 0;
}

// ─── Feed View: Scroll restore (React hook) ─────────────────────────────────

export function useSrpFeedScrollRestore({
  listingsCount,
  feedHeight,
  scrollToIndex,
  scrollerElRef,
  isMobile,
}: FeedRestoreOptions): void {
  useLayoutEffect(() => {
    const saved = readSrpScrollPosition();
    if (!saved || saved.type !== 'feed') return;

    const { index } = saved;
    if (index < 0 || index >= listingsCount) {
      clearSrpScrollPosition();
      return;
    }

    if (isMobile || window.innerWidth < MD_BREAKPOINT) {
      // MOBILE: position:fixed container + scroll-snap-type fights scrollTo.
      // Temporarily disable snap, scroll, then re-enable after layout settles.
      //
      // Key subtlety: on mobile, SearchResultsPage may briefly mount FeedView
      // with STALE data before a re-fetch replaces it. If we clear sessionStorage
      // here and the component unmounts before rAF fires, the saved position is
      // lost. We defer the clear until inside the rAF, guarded by isConnected.
      const scroller = scrollerElRef.current;
      if (!scroller) return;

      const rafId = requestAnimationFrame(() => {
        if (!scroller.isConnected) return;
        clearSrpScrollPosition();
        scroller.style.scrollSnapType = 'none';
        scrollToIndex(index, { behavior: 'instant' });

        requestAnimationFrame(() => {
          if (scroller.isConnected) {
            scroller.style.scrollSnapType = '';
          }
        });
      });

      return () => cancelAnimationFrame(rafId);
    } else {
      // DESKTOP: Wait for feedHeight to be computed so slides have correct dimensions
      if (feedHeight === null) return;
      clearSrpScrollPosition();
      scrollToIndex(index, { behavior: 'instant' });
    }
  }, [feedHeight, scrollToIndex, listingsCount, isMobile, scrollerElRef]);
}
